import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import {
  IJobType, IJobTypeCreatePayload, IJobTypeUpdatePayload,
  IJobStatusPipeline, IJobStatusPipelineCreatePayload, IJobStatusPipelineUpdatePayload,
  IJobStatusStageCreatePayload, IJobStatusStageUpdatePayload,
  IChecklistTemplate, IChecklistCreatePayload, IChecklistUpdatePayload,
  IFormTemplate, IFormTemplateCreatePayload, IFormTemplateUpdatePayload,
  ICertificationType, IRequiredCertification,
} from "../../../types/api/job-config";
import {
  listJobTypes, createJobType, updateJobType, deleteJobType,
  listPipelines, createPipeline, updatePipeline, deletePipeline,
  createStage, updateStage, deleteStage,
  listChecklists, checklistDetail, createChecklist, updateChecklist, deleteChecklist, saveChecklistItems,
  listFormTemplates, formTemplateDetail, createFormTemplate, updateFormTemplate, deleteFormTemplate,
  listCertificationTypes, listRequiredCertifications, saveRequiredCertifications,
} from "./callbacks";

// ── Job Types ──────────────────────────────────────────────────────────────
export const useJobTypeList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.JOB_TYPE_LIST, { ...params, total: null }],
    queryFn: () => listJobTypes(params).then((r) => r.data),
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IJobType) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useJobTypeCreate = (options?: { onSuccess?: (d: IJobType) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IJobTypeCreatePayload) => createJobType(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.JOB_TYPE_LIST] }); options?.onSuccess?.(res.data as IJobType); },
    onError: options?.onError,
  });
};

export const useJobTypeUpdate = (options?: { onSuccess?: (d: IJobType) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IJobTypeUpdatePayload) => updateJobType(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.JOB_TYPE_LIST] }); options?.onSuccess?.(res.data as IJobType); },
    onError: options?.onError,
  });
};

export const useJobTypeDelete = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteJobType(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.JOB_TYPE_LIST] }); options?.onSuccess?.(); },
  });
};

// ── Required Certifications ───────────────────────────────────────────────
export const useCertificationTypeList = () =>
  useQuery({
    queryKey: [API_ENDPOINTS.JOB_TYPE_CERTIFICATION_TYPES_LIST],
    queryFn: () => listCertificationTypes().then((r) => r.data),
    select: (res: any) => (res.data || []) as ICertificationType[],
    staleTime: 20000,
    refetchOnWindowFocus: false,
  });

export const useRequiredCertificationList = (jobTypeId: number | null) =>
  useQuery({
    queryKey: [API_ENDPOINTS.JOB_TYPE_REQUIRED_CERTIFICATIONS, jobTypeId, "required-certifications"],
    queryFn: () => listRequiredCertifications(jobTypeId as number).then((r) => r.data),
    select: (res: any) => (res.data || []) as IRequiredCertification[],
    enabled: !!jobTypeId,
  });

export const useSaveRequiredCertifications = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobTypeId, certification_names }: { jobTypeId: number; certification_names: string[] }) =>
      saveRequiredCertifications(jobTypeId, certification_names).then((r) => r.data),
    onSuccess: (_res, variables) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.JOB_TYPE_REQUIRED_CERTIFICATIONS, variables.jobTypeId] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

// ── Pipelines ──────────────────────────────────────────────────────────────
export const usePipelineList = (params?: any) =>
  useQuery({
    queryKey: [API_ENDPOINTS.PIPELINE_LIST, params],
    queryFn: () => listPipelines(params).then((r) => r.data),
    staleTime: 20000,
    refetchOnWindowFocus: false,
  });

export const usePipelineCreate = (options?: { onSuccess?: (d: IJobStatusPipeline) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IJobStatusPipelineCreatePayload) => createPipeline(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PIPELINE_LIST] }); options?.onSuccess?.(res.data as IJobStatusPipeline); },
    onError: options?.onError,
  });
};

export const usePipelineUpdate = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IJobStatusPipelineUpdatePayload) => updatePipeline(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PIPELINE_LIST] }); options?.onSuccess?.(); },
  });
};

export const usePipelineDelete = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePipeline(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PIPELINE_LIST] }); options?.onSuccess?.(); },
  });
};

export const useStageCreate = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IJobStatusStageCreatePayload) => createStage(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PIPELINE_LIST] }); options?.onSuccess?.(); },
  });
};

export const useStageUpdate = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IJobStatusStageUpdatePayload) => updateStage(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PIPELINE_LIST] }); options?.onSuccess?.(); },
  });
};

export const useStageDelete = (options?: { onSuccess?: () => void; onError?: (e: any, variables: { id: number; force?: boolean }) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: number; force?: boolean }) => deleteStage(id, force).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PIPELINE_LIST] }); options?.onSuccess?.(); },
    onError: (e, variables) => options?.onError?.(e, variables),
  });
};

// ── Checklists ─────────────────────────────────────────────────────────────
export const useChecklistList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.CHECKLIST_LIST, { ...params, total: null }],
    queryFn: () => listChecklists(params).then((r) => r.data),
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IChecklistTemplate) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useChecklistDetail = (id: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CHECKLIST_DETAIL, id],
    queryFn: () => checklistDetail(id).then((r) => r.data),
    enabled: !!id,
  });

export const useChecklistCreate = (options?: { onSuccess?: (d: IChecklistTemplate) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IChecklistCreatePayload) => createChecklist(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CHECKLIST_LIST] }); options?.onSuccess?.(res.data as IChecklistTemplate); },
    onError: options?.onError,
  });
};

export const useChecklistUpdate = (options?: { onSuccess?: (d: IChecklistTemplate) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IChecklistUpdatePayload) => updateChecklist(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CHECKLIST_LIST] }); options?.onSuccess?.(res.data as IChecklistTemplate); },
    onError: options?.onError,
  });
};

export const useChecklistDelete = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteChecklist(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CHECKLIST_LIST] }); options?.onSuccess?.(); },
  });
};

export const useSaveChecklistItems = (options?: { onSuccess?: (d: IChecklistTemplate) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { template_id: number; items: any[] }) => saveChecklistItems(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CHECKLIST_LIST] }); options?.onSuccess?.(res.data as IChecklistTemplate); },
  });
};

// ── Form Templates ─────────────────────────────────────────────────────────
export const useFormTemplateList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.FORM_TEMPLATE_LIST, { ...params, total: null }],
    queryFn: () => listFormTemplates(params).then((r) => r.data),
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IFormTemplate) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useFormTemplateDetail = (id: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.FORM_TEMPLATE_DETAIL, id],
    queryFn: () => formTemplateDetail(id).then((r) => r.data),
    enabled: !!id,
  });

export const useFormTemplateCreate = (options?: { onSuccess?: (d: IFormTemplate) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IFormTemplateCreatePayload) => createFormTemplate(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.FORM_TEMPLATE_LIST] }); options?.onSuccess?.(res.data as IFormTemplate); },
    onError: options?.onError,
  });
};

export const useFormTemplateUpdate = (options?: { onSuccess?: (d: IFormTemplate) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IFormTemplateUpdatePayload) => updateFormTemplate(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.FORM_TEMPLATE_LIST] }); options?.onSuccess?.(res.data as IFormTemplate); },
    onError: options?.onError,
  });
};

export const useFormTemplateDelete = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteFormTemplate(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.FORM_TEMPLATE_LIST] }); options?.onSuccess?.(); },
  });
};
